import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { tv } from '@/utils/tv';
import { USER_PROFILES_QUERY_KEY } from '@/api/queryKeys';
import { ProfileCard } from '@/components/Cards/ProfileCard';
import { Notification } from '@/components/Notification';
import { Icon } from '@/components/Icons';
import { Info } from '@/components/Icons/Info';
import { EKlaseLogo } from '@/components/Logo';
import { LogoutConfirmDialog } from '@/components/LogoutConfirmDialog';
import { AddStudentErrorModal } from '@/components/Modal/AddStudentErrorModal';
import { AddStudentOfferModal } from '@/components/Modal/AddStudentOfferModal';
import { NextStudyYearBanner } from '@/components/NextStudyYearBanner';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { StudyYearSwitcher } from '@/components/StudyYearSwitcher';
import { Tooltip } from '@/components/Tooltip';
import { useProfileSelection } from '@/pages/Profile/hooks/useProfileSelection';
import { ROUTE_PROFILE_FAQ } from '@/router/routes';
import { IconType } from '@/types/components';
import { getProfileKey } from '@/utils/person';

const TOOLTIP_MAX_WIDTH = 320;

const inactiveChevron = tv({
  base: 'text-subtle-foreground transition-transform',
  variants: {
    isExpanded: { true: 'rotate-180' },
  },
});

export const ProfileSelection: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
  const [isAddOfferModalOpen, setIsAddOfferModalOpen] = useState(false);
  const [isAddStudentErrorModalOpen, setIsAddStudentErrorModalOpen] =
    useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const {
    profiles,
    studyYears,
    selectedStudyYear,
    selectedYearIndex,
    activeProfiles,
    inactiveProfiles,
    inactiveNotificationCount,
    canAddStudent,
    pageTitle,
    selectYear,
    switchProfile,
  } = useProfileSelection();

  if (!profiles) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <PageSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background md:flex md:flex-col md:items-center md:bg-info-surface md:px-8 md:pt-14 md:pb-8">
      <EKlaseLogo className="hidden shrink-0 text-primary md:mb-16 md:block" />
      <div className="flex flex-col gap-4 md:min-h-[592px] md:w-[360px] md:rounded-xl md:bg-background md:pb-6 md:shadow-modal">
        <header className="flex shrink-0 flex-col gap-2 px-4 pt-[calc(40px+env(safe-area-inset-top))] pb-4 md:px-5 md:pt-5">
          <div className="flex items-center justify-between gap-3">
            <h1 className="text-title-30 text-foreground md:text-headline-20">
              {pageTitle}
            </h1>
            <button
              type="button"
              onClick={() => setIsLogoutConfirmOpen(true)}
              aria-label={t('global.logout')}
              className="flex size-4 shrink-0 cursor-pointer items-center justify-center text-foreground"
            >
              <Icon icon={IconType.Logout} />
            </button>
          </div>
          <p className="text-body-14 text-muted-foreground">
            {t('profile.subtitle')}
          </p>
        </header>

        <div className="flex shrink-0 items-center justify-between gap-3 px-4 md:px-5">
          <button
            type="button"
            data-tooltip-trigger
            onClick={() => setShowTooltip((prev) => !prev)}
            className="flex cursor-pointer items-center gap-1.5 text-body-14 text-muted-foreground"
          >
            <Tooltip
              content={t('profile.helpTooltip')}
              link={{ text: t('global.help'), href: ROUTE_PROFILE_FAQ }}
              isTextLeft
              maxWidth={TOOLTIP_MAX_WIDTH}
              isOpen={showTooltip}
              onOpenChange={setShowTooltip}
            >
              <span className="order-1 flex items-center md:order-none">
                <Info className="size-4 text-subtle-foreground" />
              </span>
            </Tooltip>
            <span>{t('profile.cantFind')}</span>
          </button>
          {canAddStudent && (
            <button
              type="button"
              onClick={() => setIsAddOfferModalOpen(true)}
              className="shrink-0 cursor-pointer px-1 text-subhead-14 text-primary"
            >
              {t('profile.addStudent')}
            </button>
          )}
        </div>

        <div className="relative flex flex-col gap-3 px-4 pb-[calc(24px+env(safe-area-inset-bottom))] md:px-5 md:pb-0">
          <StudyYearSwitcher
            studyYears={studyYears}
            selectedIndex={selectedYearIndex}
            onSelect={selectYear}
          />

          {selectedStudyYear?.isNext && <NextStudyYearBanner />}

          {activeProfiles.map((profile) => (
            <ProfileCard
              key={getProfileKey(profile.identity)}
              firstName={profile.user.firstName}
              lastName={profile.user.lastName}
              className={profile.user.className}
              schoolName={profile.user.schoolName}
              personType={profile.user.personType}
              notificationCount={profile.notifications.count}
              onClick={() => switchProfile(profile.identity)}
            />
          ))}

          {inactiveProfiles.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => setIsExpanded((prev) => !prev)}
                className="mt-1 flex cursor-pointer items-center justify-between gap-2"
              >
                <span className="flex items-center gap-2">
                  <span className="text-subhead-14 text-muted-foreground">
                    {t('profile.inactiveLabel')}
                  </span>
                  <Notification count={inactiveNotificationCount} />
                </span>
                <span className={inactiveChevron({ isExpanded })}>
                  <Icon icon={IconType.ChevronDown} className="size-4" />
                </span>
              </button>
              {isExpanded &&
                inactiveProfiles.map((profile) => (
                  <ProfileCard
                    key={getProfileKey(profile.identity)}
                    isInactive
                    firstName={profile.user.firstName}
                    lastName={profile.user.lastName}
                    className={profile.user.className}
                    schoolName={profile.user.schoolName}
                    personType={profile.user.personType}
                    notificationCount={profile.notifications.count}
                    onClick={() => switchProfile(profile.identity)}
                  />
                ))}
            </>
          )}
        </div>
      </div>

      {isAddOfferModalOpen && (
        <AddStudentOfferModal
          isOpen={isAddOfferModalOpen}
          onSuccess={() =>
            queryClient.invalidateQueries({ queryKey: USER_PROFILES_QUERY_KEY })
          }
          onNotFound={() => setIsAddStudentErrorModalOpen(true)}
          onClose={() => setIsAddOfferModalOpen(false)}
        />
      )}

      {isAddStudentErrorModalOpen && (
        <AddStudentErrorModal
          isOpen={isAddStudentErrorModalOpen}
          onClose={() => setIsAddStudentErrorModalOpen(false)}
        />
      )}
      <LogoutConfirmDialog
        isOpen={isLogoutConfirmOpen}
        onClose={() => setIsLogoutConfirmOpen(false)}
      />
    </div>
  );
};
